import React, { useState, FormEvent } from 'react';
import { 
  Mail, Phone, MapPin, Clock, Send, CheckCircle, 
  AlertCircle, Loader2 
} from 'lucide-react';
import './Contact.css';

interface FormData {
  name: string;
  email: string;
  organization: string;
  queryType: string;
  message: string;
}

interface FormStatus {
  type: 'idle' | 'loading' | 'success' | 'error';
  message: string;
  refId?: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    organization: '',
    queryType: '',
    message: ''
  });

  const [status, setStatus] = useState<FormStatus>({
    type: 'idle',
    message: ''
  });

  const [selectedRegion, setSelectedRegion] = useState('india');

  const regions = {
    india: {
      name: 'India (Headquarters)',
      phone: '+91-8977780644',
      address: 'Hyderabad, India',
      hours: '9:00 AM - 6:00 PM IST',
      compliance: 'IT Act Compliant'
    },
    usa: {
      name: 'USA',
      phone: '+91-8977780644',
      address: 'United States Office',
      hours: '9:00 AM - 6:00 PM EST',
      compliance: 'FDA 21 CFR Part 11 Compliant'
    },
    europe: {
      name: 'Europe',
      phone: '+91-8977780644',
      address: 'European Office',
      hours: '9:00 AM - 6:00 PM GMT',
      compliance: 'GDPR Compliant, EU Annex 11'
    },
    middleeast: {
      name: 'Middle East',
      phone: '+91-8977780644',
      address: 'Middle East Office',
      hours: '9:00 AM - 6:00 PM GST',
      compliance: 'Regional Data Protection Compliant'
    }
  };

  const queryTypes = [
    { value: 'sales', label: 'Sales Inquiry', sla: '1-2 business days' },
    { value: 'support', label: 'Technical Support', sla: '4 hours for critical' },
    { value: 'careers', label: 'Careers', sla: '3-5 business days' },
    { value: 'partnership', label: 'Partnership', sla: '2-3 business days' }
  ];

  const generateRefId = () => {
    const prefix = 'AUS';
    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substring(2, 5).toUpperCase();
    return `${prefix}-${timestamp}${random}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus({ type: 'loading', message: 'Sending your message...' });

    const refId = generateRefId();
    const selectedQuery = queryTypes.find(q => q.value === formData.queryType);
    const currentRegion = regions[selectedRegion as keyof typeof regions];

    // EmailJS configuration
    // Note: You'll need to set up EmailJS with your own service
    // 1. Create account at https://www.emailjs.com/
    // 2. Create a service (Gmail, Outlook, etc.)
    // 3. Create an email template
    // 4. Replace these IDs with your own

    // Using console.log to show what would be sent (remove in production)
    console.log('Contact form submission:', {
      to_email: 'contact@aurexatech.com',
      from_name: formData.name,
      from_email: formData.email,
      organization: formData.organization,
      query_type: selectedQuery?.label || formData.queryType,
      message: formData.message,
      ref_id: refId,
      region: currentRegion.name
    });

    try {
      // For demo purposes, we'll simulate the email send
      // In production, uncomment the emailjs.send() call and add your credentials
      
      /*
      import emailjs from '@emailjs/browser';
      
      await emailjs.send(
        'YOUR_SERVICE_ID',      // Replace with your EmailJS service ID
        'YOUR_TEMPLATE_ID',     // Replace with your EmailJS template ID
        templateParams,
        'YOUR_PUBLIC_KEY'       // Replace with your EmailJS public key
      );
      */

      // Simulating API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      setStatus({
        type: 'success',
        message: `Thank you for reaching out! We'll respond within ${selectedQuery?.sla || '2 business days'}.`,
        refId: refId
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        organization: '',
        queryType: '',
        message: ''
      });

    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Failed to send message. Please try again or email us directly at contact@aurexatech.com'
      });
    }
  };

  const currentRegion = regions[selectedRegion as keyof typeof regions];

  return (
    <div className="contact-page">
      {/* Hero */}
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero-content">
            <span className="badge">Get in Touch</span>
            <h1>Contact Aurexa</h1>
            <p>
              Have questions about our products or services? We're here to help. 
              Reach out and our team will respond promptly.
            </p>
          </div>
        </div>
      </section>

      {/* Region Selector */}
      <section className="region-section">
        <div className="container">
          <div className="region-tabs">
            {Object.entries(regions).map(([key, region]) => (
              <button
                key={key}
                className={`region-tab ${selectedRegion === key ? 'active' : ''}`}
                onClick={() => setSelectedRegion(key)}
              >
                {region.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <h2>Send Us a Message</h2>
              <p className="form-intro">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>

              {status.type === 'success' ? (
                <div className="success-message">
                  <CheckCircle size={48} />
                  <h3>Message Sent!</h3>
                  <p>{status.message}</p>
                  <p className="ref-id">Reference ID: <strong>{status.refId}</strong></p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => setStatus({ type: 'idle', message: '' })}
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="organization">Organization</label>
                      <input
                        type="text"
                        id="organization"
                        name="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        placeholder="Your Company Name"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="queryType">Query Type *</label>
                      <select
                        id="queryType"
                        name="queryType"
                        value={formData.queryType}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select query type</option>
                        {queryTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="How can we help you?"
                    />
                  </div>

                  {status.type === 'error' && (
                    <div className="error-message">
                      <AlertCircle size={20} />
                      {status.message}
                    </div>
                  )}

                  <button 
                    type="submit" 
                    className="btn btn-primary btn-submit"
                    disabled={status.type === 'loading'}
                  >
                    {status.type === 'loading' ? (
                      <>
                        <Loader2 size={18} className="spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="contact-info">
              <div className="contact-card">
                <h3>Contact Information</h3>
                <p className="contact-region-name">{currentRegion.name} Office</p>

                <div className="contact-details">
                  <div className="contact-item">
                    <Mail size={20} />
                    <div>
                      <span className="label">Email</span>
                      <a href="mailto:contact@aurexatech.com">contact@aurexatech.com</a>
                    </div>
                  </div>

                  <div className="contact-item">
                    <Mail size={20} />
                    <div>
                      <span className="label">Support</span>
                      <a href="mailto:support@aurexatech.com">support@aurexatech.com</a>
                    </div>
                  </div>

                  <div className="contact-item">
                    <Phone size={20} />
                    <div>
                      <span className="label">Phone</span>
                      <a href={`tel:${currentRegion.phone.replace(/\s/g, '')}`}>
                        {currentRegion.phone}
                      </a>
                    </div>
                  </div>

                  <div className="contact-item">
                    <MapPin size={20} />
                    <div>
                      <span className="label">Location</span>
                      <span>{currentRegion.address}</span>
                    </div>
                  </div>

                  <div className="contact-item">
                    <Clock size={20} />
                    <div>
                      <span className="label">Business Hours</span>
                      <span>{currentRegion.hours}</span>
                    </div>
                  </div>
                </div>

                <div className="compliance-note">
                  <CheckCircle size={16} />
                  {currentRegion.compliance}
                </div>
              </div>

              {/* SLA Info */}
              <div className="sla-card">
                <h4>Response Times</h4>
                <ul className="sla-list">
                  {queryTypes.map(type => (
                    <li key={type.value}>
                      <span className="sla-type">{type.label}</span>
                      <span className="sla-time">{type.sla}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
